import { logger } from "@agent-todo/shared"
import { Box, Button, Card, Center, Container, Group, Loader, Stack, Text, Title } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconLock, IconShield } from "@tabler/icons-react"
import { createFileRoute, Outlet } from "@tanstack/react-router"
import { useEffect } from "react"

import { useAuth } from "@/hooks/useAuth"

function AdminLayout() {
  const { loading, isAdmin, user, signIn, signOut } = useAuth()

  // Surface OAuth callback errors. Sign-in redirects here (/admin) after Google;
  // on failure Google appends #error=…&error_description=… to the URL. Without
  // parsing it, the failure disappears silently — the layout just shows the
  // sign-in card again with no indication anything went wrong.
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1))
    const error = hashParams.get("error")
    const errorDescription = hashParams.get("error_description")
    if (!error) return

    const message = errorDescription ? errorDescription.replace(/\+/g, " ") : error.replace(/_/g, " ")
    notifications.show({
      color: "red",
      title: "Sign-in failed",
      message,
      autoClose: 8000,
    })
    logger.warn("OAuth sign-in error surfaced on admin layout", {
      operation: "admin-oauth-error-display",
      component: "AdminLayout",
      error,
      errorDescription: errorDescription ?? undefined,
    })
    // Strip the error hash so a refresh doesn't re-fire the toast.
    window.history.replaceState(null, "", window.location.pathname)
  }, [])

  if (loading) {
    return (
      <Center h="100vh">
        <Box ta="center">
          <Loader size="lg" mb="md" />
          <Text>Loading authentication...</Text>
        </Box>
      </Center>
    )
  }

  if (!user) {
    return (
      <Container size="sm" py="xl">
        <Center h="80vh">
          <Card shadow="md" padding="xl" radius="md" w="100%" maw={400}>
            <Group mb="lg">
              <IconShield size={32} color="var(--mantine-color-blue-6)" />
              <Box>
                <Title order={3}>Agent Gate Admin</Title>
                <Text size="sm" c="dimmed">
                  Admin access required
                </Text>
              </Box>
            </Group>

            <Text mb="lg">Sign in with your admin account to access the system monitoring dashboard.</Text>

            <Stack gap="sm">
              <Button
                leftSection={<IconShield size={16} />}
                onClick={() => {
                  void signIn("google")
                }}
                fullWidth
              >
                Sign in with Google
              </Button>
              <Button
                leftSection={<IconShield size={16} />}
                onClick={() => {
                  void signIn("github")
                }}
                variant="outline"
                fullWidth
              >
                Sign in with GitHub
              </Button>
            </Stack>
          </Card>
        </Center>
      </Container>
    )
  }

  if (!isAdmin) {
    return (
      <Container size="sm" py="xl">
        <Center h="80vh">
          <Card shadow="md" padding="xl" radius="md" w="100%" maw={400}>
            <Group mb="lg">
              <IconLock size={32} color="var(--mantine-color-red-6)" />
              <Box>
                <Title order={3}>Access Denied</Title>
                <Text size="sm" c="dimmed">
                  Admin privileges required
                </Text>
              </Box>
            </Group>

            <Text mb="lg">
              You don't have the necessary permissions to access this monitoring dashboard. Only admin and support users
              can view system status.
            </Text>

            <Button
              onClick={() => {
                void signOut()
              }}
              variant="outline"
              fullWidth
            >
              Sign out
            </Button>
          </Card>
        </Center>
      </Container>
    )
  }

  return <Outlet />
}

export const Route = createFileRoute("/_admin")({
  component: AdminLayout,
})
